import React, { useEffect, useMemo, useState } from "react";
import { RecommendedAudience, RecommendedAudiencesProps } from "./RecommendedAudiences.types";
import { useSelector } from "react-redux";
import { localize } from "src/l10n";
import { CaptionBolder, CaptionBody } from "../Text";
import classNames from "classnames";
import { DefaultButton } from "@fluentui/react";
import api, { getCancelTokenSource } from "src/api";

const RecommendedAudiences: React.FC<RecommendedAudiencesProps> = ({ onAudienceSelected, targetIds }) => {
    const activeUser = useSelector<Spintr.AppState, Spintr.IActiveUser>(
        (appState) => appState.profile.active,
    );

    const enableCompanyLevel = useSelector<Spintr.AppState, boolean>(
        (appState) => appState.instance.get("enableCompanyLevel") as boolean,
    );

    const [companyGroups, setCompanyGroups] = useState<RecommendedAudience[]>([]);

    useEffect(() => {
        if (!enableCompanyLevel) {
            setCompanyGroups([]);
            return;
        }

        const cancelSource = getCancelTokenSource();
        const baseCompanyGroups: RecommendedAudience[] = [{
            id: 0,
            name: localize("Alla"),
            key: 0,
        }];

        setCompanyGroups(baseCompanyGroups);

        api.get("/api/v1/companies", {
            params: {
                limit: 1000,
                includeDeleted: false,
            },
            cancelToken: cancelSource.token,
        }).then((response) => {
            const companies: RecommendedAudience[] = (response?.data?.items || [])
                .filter((company: any) => company?.id && company?.name)
                .map((company: any) => ({
                    id: company.id,
                    name: company.name,
                    info: localize("Foretag"),
                    subText: localize("Foretag"),
                    key: company.id,
                    type: 65,
                }));

            setCompanyGroups([
                ...baseCompanyGroups,
                ...companies.filter((company) => !baseCompanyGroups.some((base) => base.id === company.id)),
            ]);
        }).catch(() => {
            setCompanyGroups(baseCompanyGroups);
        });

        return () => cancelSource.cancel("component unmounted");
    }, [enableCompanyLevel]);

    const filteredCompanyGroups = useMemo(
        () => companyGroups.filter((audience) => !targetIds.includes(audience.id)),
        [companyGroups, targetIds]
    );

    const memberGroups = useMemo(
        () => {
            const memberGroups: RecommendedAudience[] = !enableCompanyLevel ? [{
                id: 0,
                name: localize("Alla"),
                key: 0,
            }] : [];

            const addMemberGroup = (audience: RecommendedAudience) => {
                if (!memberGroups.some((existing) => existing.id === audience.id)) {
                    memberGroups.push(audience);
                }
            };

            const department = activeUser?.department;
            const office = department?.office;
            const company = office?.company;

            if (department?.id && department.name) {
                addMemberGroup({
                    id: department.id,
                    name: department.name,
                    info: localize("Avdelning"),
                    subText: localize("Avdelning"),
                    key: department.id,
                    type: 2,
                });
            }

            if (office?.id && office.name) {
                addMemberGroup({
                    id: office.id,
                    name: office.name,
                    info: localize("Kontor"),
                    subText: localize("Kontor"),
                    key: office.id,
                    type: 8,
                });
            }

            if (company?.id && company.name && !enableCompanyLevel) {
                addMemberGroup({
                    id: company.id,
                    name: company.name,
                    info: localize("Foretag"),
                    subText: localize("Foretag"),
                    key: company.id,
                    type: 65,
                });
            }
            return memberGroups;
        },
        [activeUser, enableCompanyLevel]
    );

    return (
        <div className="RecommendedAudiences">
            <CaptionBolder color="contentDark">
                {localize("YourTargetAudience")}
            </CaptionBolder>
            {enableCompanyLevel && filteredCompanyGroups.length > 0 && (
                <div className="TargetGroupList">
                    <CaptionBody color="grayDark" className="TargetGroupList-title">
                        {localize("YourCompanies")}
                    </CaptionBody>
                    <div className="TargetGroupList-items">
                        {filteredCompanyGroups.map((audience) => (
                            <DefaultButton
                                key={audience.key}
                                onClick={() => onAudienceSelected(audience)}
                                className={classNames("TargetGroupItem", {
                                    "selected": targetIds.includes(audience.id) ||
                                        (targetIds.length === 0 && audience.id < 1),
                                })}
                            >
                                {audience.name}
                            </DefaultButton>
                        ))}
                    </div>
                </div>
            )}
            <div className="TargetGroupList">
                <CaptionBody color="grayDark" className="TargetGroupList-title">
                    {localize("YourTargetGroups")}
                </CaptionBody>
                <div className="TargetGroupList-items">
                    {memberGroups.map((audience) => (
                        <DefaultButton
                            key={audience.id}
                            onClick={() => onAudienceSelected(audience)}
                            className={classNames("TargetGroupItem", {
                                "selected": targetIds.includes(audience.id) ||
                                    (targetIds.length === 0 && audience.id < 1),
                            })}
                        >
                            {audience.name}
                        </DefaultButton>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default RecommendedAudiences;